import type { components } from '@/generated/core-api'
import { readPublishedLocalizedText, type PublishedLocalizedText } from '@/shared/i18n/publishedMetadata'

import { CoreError, isCoreError } from './errors'
import { API_ROOT, asRecord, request } from './transport'

/**
 * The Control Center's authorized module catalogue: visible registrations,
 * plus hidden registrations for developers, and the command that moves their
 * state. The browser reads registration and lifecycle from HERE and nowhere
 * else — not from a Patient's external identities, not from the frontend's
 * own contribution list, not from anything it stored.
 *
 * The decoders narrow the generated wire types to what the page renders and
 * keep them DEFENSIVE against a newer server. A health status, egress family
 * or actor kind this build does not know is kept as the string it was, never
 * dropped and never decayed into a known value: the page shows an
 * unrecognized value as such, because "not reported" and "reported something
 * this build cannot name" are different facts to an administrator.
 *
 * Clinic scope is never a parameter: the server derives it from the session.
 */

type WireItem = components['schemas']['ClinicModuleCatalogueItem']
type WireCatalogue = components['schemas']['ClinicModuleCatalogueResponse']
type WireCommand = components['schemas']['UpdateClinicModuleStateRequest']

export type ClinicModuleHealthStatus =
  WireItem['configurationHealth']['status']
export type ClinicModuleEgressFamily = WireItem['egressFamilies'][number]
export type ClinicModuleActorKind = NonNullable<WireItem['lastChange']>['actorKind']

export const CLINIC_MODULE_HEALTH_STATUSES: readonly ClinicModuleHealthStatus[] = [
  'ready',
  'configuration_required',
  'unavailable',
  'unknown',
]

export const CLINIC_MODULE_EGRESS_FAMILIES: readonly ClinicModuleEgressFamily[] = [
  'ai',
  'communications',
  'patient_message',
  'outbound_http',
  'notification',
  'background_dispatch',
]

export const CLINIC_MODULE_ACTOR_KINDS: readonly ClinicModuleActorKind[] = [
  'account',
  'integration',
]

/** The clinic's effective lifecycle state and the revision a change must quote. */
export interface ClinicModuleState {
  readonly enabled: boolean
  readonly hidden: boolean
  readonly revision: number
}

export interface ClinicModuleCatalogueItem {
  readonly moduleCode: string
  /** Canonical server-published localized values. */
  readonly displayName: PublishedLocalizedText
  readonly description: PublishedLocalizedText
  readonly presentationKey: string
  /** A trusted key into the repository's own asset registry — never a URL. */
  readonly iconKey: string
  readonly moduleVersion: string
  readonly buildIdentity: string
  readonly coreServiceDependencies: readonly string[]
  /** Known families, plus any string a newer server declares. */
  readonly egressFamilies: readonly string[]
  readonly state: ClinicModuleState
  readonly lastChange: { readonly changedAt: string; readonly actorKind: string } | null
  readonly configurationHealth: {
    /** A known status, or the string a newer server sent. */
    readonly status: string
    readonly issueCodes: readonly string[]
  }
  /** Server-derived for THIS request's grant; the page never infers it. */
  readonly canChangeState: boolean
  /** Only a developer may open or hide this organization's module. */
  readonly canChangeVisibility: boolean
}

export interface ClinicModuleStateCommand {
  readonly enabled: boolean
  readonly hidden: boolean
  readonly revision: number
  readonly reason: string
}

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((entry) => typeof entry === 'string')

function stateOf(value: unknown, moduleCode: string): ClinicModuleState {
  const record = asRecord(value)
  if (
    !record ||
    typeof record['enabled'] !== 'boolean' ||
    typeof record['hidden'] !== 'boolean' ||
    typeof record['revision'] !== 'number' ||
    !Number.isInteger(record['revision'])
  ) {
    throw new CoreError('malformed', `module ${moduleCode} carried no lifecycle state`)
  }
  return {
    enabled: record['enabled'],
    hidden: record['hidden'],
    revision: record['revision'],
  }
}

function itemOf(value: unknown): ClinicModuleCatalogueItem {
  const wire = asRecord(value) as Partial<WireItem> | null
  if (!wire || typeof wire.moduleCode !== 'string' || wire.moduleCode === '') {
    throw new CoreError('malformed', 'module catalogue item carried no module code')
  }
  const health = asRecord(wire.configurationHealth)
  const lastChange = asRecord(wire.lastChange)
  return {
    moduleCode: wire.moduleCode,
    displayName: readPublishedLocalizedText(wire.displayName, 160)!,
    description: readPublishedLocalizedText(wire.description, 500)!,
    presentationKey: typeof wire.presentationKey === 'string' ? wire.presentationKey : '',
    iconKey: typeof wire.iconKey === 'string' ? wire.iconKey : '',
    moduleVersion: typeof wire.moduleVersion === 'string' ? wire.moduleVersion : '',
    buildIdentity: typeof wire.buildIdentity === 'string' ? wire.buildIdentity : '',
    coreServiceDependencies: isStringArray(wire.coreServiceDependencies)
      ? [...wire.coreServiceDependencies]
      : [],
    egressFamilies: isStringArray(wire.egressFamilies) ? [...wire.egressFamilies] : [],
    state: stateOf(wire.state, wire.moduleCode),
    lastChange:
      lastChange && typeof lastChange['changedAt'] === 'string'
        ? {
            changedAt: lastChange['changedAt'],
            actorKind:
              typeof lastChange['actorKind'] === 'string' ? lastChange['actorKind'] : '',
          }
        : null,
    configurationHealth: {
      status: health && typeof health['status'] === 'string' ? health['status'] : 'unknown',
      issueCodes: health && isStringArray(health['issueCodes']) ? [...health['issueCodes']] : [],
    },
    canChangeState: wire.canChangeState === true,
    canChangeVisibility: wire.canChangeVisibility === true,
  }
}

/**
 * The catalogue read. A side-effect-free read on the server by contract; on
 * this side it is one request and nothing else — no per-module fan-out, no
 * projection read, no module data.
 */
export async function getClinicModules(
  signal?: AbortSignal,
): Promise<readonly ClinicModuleCatalogueItem[]> {
  const body = (await request('GET', `${API_ROOT}/clinic/modules`, {
    ...(signal ? { signal } : {}),
  })) as Partial<WireCatalogue> | undefined
  if (!body || !Array.isArray(body.items)) {
    throw new CoreError('malformed', 'module catalogue carried no items array')
  }
  return body.items.map(itemOf)
}

/**
 * The one state command. `enabled=true, hidden=true` is refused by the server
 * at every boundary; this client refuses it too, before any request, so a
 * caller bug cannot even ask. The idempotency key is the caller's — one act,
 * one key, reused on retry — and the revision is the one the caller last saw.
 */
export async function updateClinicModuleState(
  moduleCode: string,
  command: ClinicModuleStateCommand,
  idempotencyKey: string,
): Promise<ClinicModuleCatalogueItem> {
  if (command.enabled && command.hidden) {
    throw new CoreError('invalid-input', 'a module cannot be enabled and hidden at once')
  }
  const body: WireCommand = {
    enabled: command.enabled,
    hidden: command.hidden,
    revision: command.revision,
    reason: command.reason,
  }
  const response = await request(
    'PUT',
    `${API_ROOT}/clinic/modules/${encodeURIComponent(moduleCode)}/state`,
    { body, idempotencyKey: idempotencyKey },
  )
  return itemOf(response)
}

/**
 * The authoritative state a stale write was refused with. Present only on
 * `module_state_changed_concurrently`, and validated field by field because
 * the error envelope is a structured detail no decoder has yet checked.
 */
export function concurrentModuleState(error: unknown): ClinicModuleState | null {
  if (!isCoreError(error) || error.code !== 'module_state_changed_concurrently') {
    return null
  }
  const current = asRecord(error.details?.['currentModuleState'])
  if (
    !current ||
    typeof current['enabled'] !== 'boolean' ||
    typeof current['hidden'] !== 'boolean' ||
    typeof current['revision'] !== 'number'
  ) {
    return null
  }
  return {
    enabled: current['enabled'],
    hidden: current['hidden'],
    revision: current['revision'],
  }
}
